import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import {
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  TablePagination,
} from '@mui/material';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import PageHeader1 from '../../components/common/PageHeader1';
import { formatUTCDateForExport, formatUTCDate, convertISTToUTCForAPI } from '../../utils/dateUtils';
// Mixpanel tracking removed

function BookingList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [selectedBookingType, setSelectedBookingType] = useState('All');
  const [appliedBookingType, setAppliedBookingType] = useState('All');
  const [selectedBookingStatus, setSelectedBookingStatus] = useState('All');
  const [appliedBookingStatus, setAppliedBookingStatus] = useState('All');
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const todayDate = getTodayDate();

  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('userName'); // "userName", "astrologerName"
  const [appliedSearchType, setAppliedSearchType] = useState('userName');
  const [startDate, setStartDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [paginationDetail, setPaginationDetail] = useState({});
  const [lastAction, setLastAction] = useState(null); // "apply" to track when Apply Filters was clicked
  const [mobileNumber, setMobileNumber] = useState(null);
  const isInitialMount = useRef(true);
  const lastParamsRef = useRef(null);
  const token = localStorage.getItem('astroVani-admin-token');

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const redirectProcessed = useRef(false);

  // Check for redirect parameter on mount and auto-search
  useEffect(() => {
    if (redirectProcessed.current) return;

    const redirectParam = searchParams.get('redirect');
    if (redirectParam) {
      redirectProcessed.current = true;
      setMobileNumber(redirectParam);
      setSearchQuery(redirectParam);
      setAppliedSearchQuery(redirectParam);
      setSearchType('userName');
      setAppliedSearchType('userName');
      setLastAction("apply");
      setPaginationModel(prev => ({ ...prev, page: 0 }));
      // Remove redirect parameter from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('redirect');
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const statusOptions = [
    'accepted', 'rejected', 'cancelled', 'pending',
    'ongoing', 'completed', 'missed', 'disConnected'
  ];

  useEffect(() => {
    // Skip initial load if redirect is being processed
    if (redirectProcessed.current && isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const apiPage = paginationModel.page + 1;
        let limit = Number(paginationModel.pageSize);
        if (isNaN(limit)) {
          limit = 10000; // Use fixed default
        }

        const params = new URLSearchParams({
          page: apiPage.toString(),
          limit: limit.toString(),
        });

        // Date logic:
        // 1. If dates are applied (from Apply Filter), use them
        // 2. If no dates applied and it's initial load (lastAction is null), use today's date
        // 3. If no dates applied and Apply Filter was clicked (lastAction is "apply"), don't send dates
        if (appliedStartDate && appliedEndDate) {
          // Dates were explicitly applied - use them
          params.append('startDate', convertISTToUTCForAPI(appliedStartDate, 'start'));
          params.append('endDate', convertISTToUTCForAPI(appliedEndDate, 'end'));
        } else if (lastAction === null) {
          // Initial load or pagination change with no dates - send today's date by default
          params.append('startDate', convertISTToUTCForAPI(todayDate, 'start'));
          params.append('endDate', convertISTToUTCForAPI(todayDate, 'end'));
        }
        // If lastAction is "apply" and no dates, don't send dates

        // Add booking type filter if selected
        if (appliedBookingType !== 'All') {
          params.append('type', appliedBookingType);
        }

        // Add booking status filter if selected
        if (appliedBookingStatus !== 'All') {
          params.append('status', appliedBookingStatus);
        }

        // If mobileNumber is present (from redirect), use it directly, otherwise use search
        if (mobileNumber) {
          params.append('mobileNumber', mobileNumber);
        } else if (appliedSearchQuery && appliedSearchQuery.trim()) {
          if (appliedSearchType === "userName") {
            params.append('userName', appliedSearchQuery);
          } else if (appliedSearchType === "astrologerName") {
            params.append('astrologerName', appliedSearchQuery);
          } else {
            params.append('search', appliedSearchQuery);
          }
        }

        // Prevent duplicate API calls by comparing params
        const paramsString = params.toString();
        if (lastParamsRef.current === paramsString && !isInitialMount.current) {
          setLoading(false);
          return; // Skip if params haven't changed
        }

        lastParamsRef.current = paramsString;
        isInitialMount.current = false;

        const requestUrl = `${process.env.REACT_APP_BASEURL}/admin/booking?${params.toString()}`;
        const response = await axios.get(requestUrl, {
          headers: { Authorization: token },
        });

        const bookings = response?.data?.data || [];
        const pagination = response?.data?.paginationDetail || {};

        const data = bookings.map((item, index) => {
          const formatCamelCase = (str) => {
            if (!str) return '';
            return str
              .replace(/([a-z])([A-Z])/g, '$1 $2')
              .replace(/^./, (char) => char.toUpperCase());
          };

          return {
            sono: paginationModel.page * paginationModel.pageSize + index + 1,
            orderId: item?.orderId,
            userName: item?.userName,
            bookingId: item?.bookingId,
            astroName: item?.astroName,
            bookingTypeRaw: item?.bookingType,
            bookingType: formatCamelCase(item?.bookingType),
            sessionType: item?.sessionType || '-',
            bookingStatus: item?.bookingStatus,
            endBy: item?.endBy,
            totalDeduction: Number(item?.totalDeduction || 0),
            totalDuration:
              Number(item?.totalDuration || 0),
            actualDeduction:
              item?.actualDeduction != null
                ? `${Number(item.actualDeduction).toFixed(2)}`
                : 'N/A',
            promoDeduction:
              item?.promotionalDeduction != null
                ? `${Number(item.promotionalDeduction).toFixed(2)}`
                : 'N/A',
            isConvertedFromFreeChat: item?.isConvertedFromFreeChat === true,
            isAudioAvailable: Boolean(
              (typeof item?.audioUrl === 'string' && item.audioUrl.trim() !== '' && item.audioUrl.trim() !== 'null' && item.audioUrl.trim() !== 'undefined') ||
              item?.isAudioAvailable === true
            ),
            createdAt: formatUTCDateForExport(item?.createdAt),
            duration: item?.totalDuration != null ? Number(item.totalDuration) : 0,
          };
        });

        setRows(data);
        setPaginationDetail(pagination);
      } catch (error) {
        console.error('Error fetching data:', error);
        setRows([]);
        setPaginationDetail({});
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchData();
    } else {
      setLoading(false);
      setRows([]);
      setPaginationDetail({});
    }
  }, [paginationModel.page, paginationModel.pageSize, appliedBookingType, appliedBookingStatus, appliedSearchQuery, appliedSearchType, appliedStartDate, appliedEndDate, lastAction, todayDate, token, mobileNumber]);


  const handleChangePage = (event, newPage) => {
    setPaginationModel({ ...paginationModel, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const value = event.target.value;
    const newPageSize = parseInt(value, 10);
    setPaginationModel({ page: 0, pageSize: newPageSize });
  };

  // Apply Filters Handler - Applies search, date filters, and booking type/status together
  const handleApplyFilters = () => {
    setMobileNumber(null); // Clear mobileNumber when manually applying filters
    setAppliedSearchQuery(searchQuery.trim());
    setAppliedSearchType(searchType);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedBookingType(selectedBookingType);
    setAppliedBookingStatus(selectedBookingStatus);
    setLastAction("apply");
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleResetFilter = () => {
    setSearchQuery('');
    setAppliedSearchQuery('');
    setSearchType('userName');
    setAppliedSearchType('userName');
    setStartDate("");
    setEndDate("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setSelectedBookingType('All');
    setAppliedBookingType('All');
    setSelectedBookingStatus('All');
    setAppliedBookingStatus('All');
    setMobileNumber(null);
    setLastAction(null);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();

      // Add booking type filter if selected
      if (appliedBookingType !== 'All') {
        params.append('type', appliedBookingType);
      }

      // Add booking status filter if selected
      if (appliedBookingStatus !== 'All') {
        params.append('status', appliedBookingStatus);
      }

      // Send dates - use today's date if not provided
      const exportStartDate = appliedStartDate || todayDate;
      const exportEndDate = appliedEndDate || todayDate;
      params.append('startDate', convertISTToUTCForAPI(exportStartDate, 'start'));
      params.append('endDate', convertISTToUTCForAPI(exportEndDate, 'end'));

      // If mobileNumber is present (from redirect), use it directly, otherwise use search
      if (mobileNumber) {
        params.append('mobileNumber', mobileNumber);
      } else if (appliedSearchQuery && appliedSearchQuery.trim()) {
        if (appliedSearchType === "userName") {
          params.append('userName', appliedSearchQuery);
        } else if (appliedSearchType === "astrologerName") {
          params.append('astrologerName', appliedSearchQuery);
        } else {
          params.append('search', appliedSearchQuery);
        }
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/bookingExport?${params.toString()}`,
        {
          headers: { Authorization: token },
        }
      );

      const bookings = response?.data?.data || [];

      if (bookings.length === 0) {
        return;
      }

      const formatCamelCase = (str) => {
        if (!str) return '';
        return str
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/^./, (char) => char.toUpperCase());
      };

      const exportData = bookings.map((item, index) => ({
        'S.No': index + 1,
        'Order ID': item?.orderId || '',
        'User Name': item?.userName || '',
        'Astrologer Name': item?.astroName || '',
        'Booking Type': formatCamelCase(item?.bookingType) || '',
        'Session Type': item?.sessionType || '-',
        'End By': item?.endBy || '',
        'Booking Status': item?.bookingStatus || '',
        'Total Deduction': (Number(item?.totalDeduction || 0)).toFixed(2),
        'Actual Deduction': item?.actualDeduction != null ? Number(item?.actualDeduction).toFixed(2) : 'N/A',
        'Promotional Deduction': item?.promotionalDeduction != null ? Number(item?.promotionalDeduction).toFixed(2) : 'N/A',
        'Total Duration': item?.totalDuration != null ? Number(item?.totalDuration) : 0,
        'Converted From Free Chat': item?.isConvertedFromFreeChat ? 'Yes' : 'No',
        'Audio Available': Boolean(
          (typeof item?.audioUrl === 'string' && item.audioUrl.trim() !== '' && item.audioUrl.trim() !== 'null' && item.audioUrl.trim() !== 'undefined') ||
          item?.isAudioAvailable === true
        ) ? 'True' : 'False',
        'Created Date': formatUTCDate(item?.createdAt, { format: 'date', separator: '/' }),
        'Created Time': formatUTCDate(item?.createdAt, { format: 'time', showSeconds: true }),
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Bookings');

      const columnWidths = [
        { wch: 8 },
        { wch: 20 },
        { wch: 20 },
        { wch: 25 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 22 },
        { wch: 20 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
      ];
      worksheet['!cols'] = columnWidths;

      XLSX.writeFile(workbook, 'bookings_export.xlsx');
      toast.success('Bookings exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export bookings');
    } finally {
      setIsExporting(false);
    }
  };

  const deleteBooking = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_BASEURL}/admin/booking/${id}`, {
        headers: { Authorization: token },
      });
      const { page, pageSize } = paginationModel;
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: pageSize.toString(),
      });
      if (selectedBookingType !== 'All') params.append('type', selectedBookingType);
      if (selectedBookingStatus !== 'All') params.append('status', selectedBookingStatus);
      if (searchQuery) params.append('search', searchQuery);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/booking?${params.toString()}`,
        { headers: { Authorization: token } }
      );
      const bookings = response?.data?.data || [];
      const pagination = response?.data?.paginationDetail || {};
      const formatCamelCase = (str) => {
        if (!str) return '';
        return str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (char) => char.toUpperCase());
      };
      const data = bookings.map((item, index) => ({
        sono: page * pageSize + index + 1,
        orderId: item?.orderId,
        userName: item?.userName,
        bookingId: item?.bookingId,
        astroName: item?.astroName,
        bookingTypeRaw: item?.bookingType,
        endBy: item?.endBy || '-',
        bookingType: formatCamelCase(item?.bookingType),
        sessionType: item?.sessionType || '-',
        bookingStatus: item?.bookingStatus,
        totalDeduction: Number(item?.totalDeduction || 0),
        totalDuration: Number(item?.totalDuration || 0),
        actualDeduction: item?.actualDeduction != null ? `${Number(item.actualDeduction).toFixed(2)}` : 'N/A',
        promoDeduction: item?.promotionalDeduction != null ? `${Number(item.promotionalDeduction).toFixed(2)}` : 'N/A',
        duration: item?.totalDuration != null ? Number(item.totalDuration) : 0,
        createdAt: item?.createdAt ? new Date(item?.createdAt).toLocaleString() : '',
      }));
      setRows(data);
      setPaginationDetail(pagination);
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleClickOpen = (userId) => {
    setSelectedUserId(userId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUserId(null);
  };

  const handleDelete = async () => {
    if (selectedUserId) {
      await deleteBooking(selectedUserId);
    }
    handleClose();
  };

  const filteredRows = useMemo(() => {
    const pageSize = paginationModel.pageSize;
    return rows.map((item, index) => ({
      ...item,
      sono: paginationModel.page * pageSize + index + 1,
    }));
  }, [rows, paginationModel]);

  const columns = [
    {
      field: 'sono',
      headerName: 'S.NO',
      width: 80,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <IconButton
            size="small"
            component={Link}
            to={`/booking-details/${row.bookingId}`}
            title="View Details"
            sx={{
              color: '#d32f2f',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
              },
            }}
          >
            <i className="icofont-eye" style={{ fontSize: '18px' }}></i>
          </IconButton>
          {/* <IconButton 
            size="small"
            onClick={() => handleClickOpen(row.bookingId)} 
            title="Delete Booking"
            sx={{
              color: '#d32f2f',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
              },
            }}
          >
            <i className="icofont-ui-delete" style={{ fontSize: '18px' }}></i>
          </IconButton> */}
        </Box>
      ),
    },
    { field: 'orderId', headerName: 'Order ID', width: 170 },
    { field: 'userName', headerName: 'User Name', width: 200 },
    { field: 'astroName', headerName: 'Astrologer Name', width: 200 },
    { field: 'endBy', headerName: 'End By', width: 150 },
    { field: 'bookingType', headerName: 'Booking Type', width: 180 },
    { field: 'sessionType', headerName: 'Session Type', width: 150 },
    { field: 'bookingStatus', headerName: 'Booking Status', width: 180 },
    { field: 'totalDeduction', headerName: 'Total Deduction', width: 180 },
    { field: 'actualDeduction', headerName: 'Actual Deduction', width: 180 },
    { field: 'promoDeduction', headerName: 'Promotional Deduction', width: 200 },
    { field: 'duration', headerName: 'Total Duration', width: 180 },
    { field: 'isConvertedFromFreeChat', headerName: 'Converted From Free Chat', width: 200, renderCell: (params) => (params.value ? 'Yes' : 'No') },
    {
      field: 'isAudioAvailable',
      headerName: 'Audio Available',
      width: 150,
      renderCell: (params) => (
        <Box
          sx={{
            color: params.value ? '#2e7d32' : '#d32f2f',
            fontWeight: 600,
          }}
        >
          {params.value ? 'True' : 'False'}
        </Box>
      ),
    },
    { field: 'createdAt', headerName: 'Created At', width: 200 },
  ];

  return (
    <div className="body d-flex">
      <div className="container-xxl">
        <PageHeader1 pagetitle="Bookings List" />

        {/* Toolbar with Filters */}
        <div className="card mb-3" style={{
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body" style={{ padding: '16px 20px' }}>
            {/* First Row: All Inputs in Single Line */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 140, flexShrink: 0 }}>
                <InputLabel>Search By</InputLabel>
                <Select
                  value={searchType}
                  label="Search By"
                  onChange={(e) => setSearchType(e.target.value)}
                  sx={{
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.87)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--primary-color, #E7B242)',
                    },
                  }}
                >
                  <MenuItem value="userName">User Name</MenuItem>
                  <MenuItem value="astrologerName">Astrologer Name</MenuItem>
                </Select>
              </FormControl>
              <TextField
                placeholder={
                  searchType === "userName" ? "Search by user name..." :
                    "Search by astrologer name..."
                }
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') { handleApplyFilters(); } }}
                sx={{
                  width: { xs: '150px', sm: '180px', md: '200px' },
                  flexShrink: 0,
                  background: 'var(--card-color)',
                  borderRadius: '9px',
                  '& .MuiInputBase-input': { fontSize: '13px', padding: '9px 10px 9px 0' },
                }}
              />

              <TextField
                label="Start Date"
                type="date"
                variant="outlined"
                size="small"
                value={startDate || ''}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  minWidth: 160,
                  flexShrink: 0,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 8px 12px',
                    fontSize: '14px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '14px',
                  },
                }}
              />

              <TextField
                label="End Date"
                type="date"
                variant="outlined"
                size="small"
                value={endDate || ''}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  minWidth: 160,
                  flexShrink: 0,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 8px 12px',
                    fontSize: '14px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '14px',
                  },
                }}
              />

              <FormControl size="small" sx={{
                minWidth: '160px',
                flexShrink: 0,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}>
                <InputLabel>Booking Type</InputLabel>
                <Select
                  value={selectedBookingType}
                  label="Booking Type"
                  onChange={(e) => setSelectedBookingType(e.target.value)}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="chat">Chat</MenuItem>
                  <MenuItem value="call">Call</MenuItem>
                  <MenuItem value="videocall">Video Call</MenuItem>
                  <MenuItem value="physicalvisit">Physical Visit</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{
                minWidth: '160px',
                flexShrink: 0,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}>
                <InputLabel>Booking Status</InputLabel>
                <Select
                  value={selectedBookingStatus}
                  label="Booking Status"
                  onChange={(e) => setSelectedBookingStatus(e.target.value)}
                >
                  <MenuItem value="All">All</MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Second Row: Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                disabled={!searchQuery.trim() && !startDate && !endDate && selectedBookingType === 'All' && selectedBookingStatus === 'All'}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  backgroundColor: "var(--primary-color, #E7B242)",
                  color: "#fff",
                  height: 40,
                  px: 2,
                  flexShrink: 0,
                  "&:hover": { backgroundColor: "var(--primary-color, #E7B242)", opacity: 0.9 },
                  "&:disabled": {
                    backgroundColor: "rgba(0, 0, 0, 0.12)",
                    color: "rgba(0, 0, 0, 0.26)",
                  },
                }}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                onClick={handleResetFilter}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  borderColor: 'var(--primary-color, #E7B242)',
                  color: 'var(--primary-color, #E7B242)',
                  height: 40,
                  px: 2,
                  '&:hover': {
                    borderColor: 'var(--primary-color, #E7B242)',
                    backgroundColor: 'rgba(231, 178, 66, 0.08)',
                  },
                }}
              >
                Reset
              </Button>

              <Button
                variant="contained"
                onClick={exportToExcel}
                disabled={isExporting}
                startIcon={isExporting ? <CircularProgress size={16} /> : null}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  backgroundColor: 'var(--primary-color, #E7B242)',
                  height: 40,
                  px: 2,
                  '&:hover': {
                    backgroundColor: 'var(--primary-color, #E7B242)',
                    opacity: 0.9,
                  },
                  '&:disabled': {
                    backgroundColor: 'var(--primary-color, #E7B242)',
                    opacity: 0.6,
                  },
                }}
              >
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
            </Box>
          </div>
        </div>

        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card" style={{
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: 'none',
              overflow: 'hidden'
            }}>
              <div className="card-body" style={{ padding: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #e9ecef",
                  }}
                >
                  <TablePagination
                    component="div"
                    count={paginationDetail?.totalDocs || 0}
                    page={paginationModel.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={paginationModel.pageSize}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                    labelRowsPerPage="Rows per page:"
                    sx={{
                      borderTop: '1px solid #e9ecef',
                      '& .MuiTablePagination-toolbar': {
                        padding: '4px 20px',
                      },
                    }}
                  />
                </Box>
                <Box sx={{
                  // height: 600,
                  width: '100%',
                  '& .MuiDataGrid-root': {
                    border: 'none',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f0f0f0',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #e9ecef',
                    fontWeight: 600,
                  },
                  '& .MuiDataGrid-footerContainer': {
                    display: 'none',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#f8f9fa',
                  },
                }}>
                  <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    getRowId={(row) => row.bookingId}
                    loading={loading}
                    paginationMode="server"
                    hideFooterPagination
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={(newModel) =>
                      setColumnVisibilityModel(newModel)
                    }
                    autoHeight
                    sx={{
                      '& .MuiDataGrid-cell:focus': {
                        outline: 'none',
                      },
                      '& .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                      },
                    }}
                  />
                </Box>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this booking?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleDelete} color="error">Delete</Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}

export default BookingList;
